import { NetworkIcon } from '@application/components';
import { Site } from '@domain/graphql.types';
import { ChevronRight } from '@mui/icons-material';
import { Box, ButtonBase, Typography } from '@mui/material';
import { forwardRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import MapPin, { getMapPinVariant, mapPinVariantLabelKey } from '../components/MapPin';
import { getAvailability } from '../components/realTimeLocation';
import SiteLookupContext from '../context/SiteLookupContext';
import SiteLookupMapContext from '../context/SiteLookupMapContext';
import { siteLookupTokens } from '../siteLookupTokens';
import StationTag, { AvailabilityTag } from './StationTag';
import { getExportStatus, getPortsLabel } from './stationSummary';

const { color, size } = siteLookupTokens;

type Props = {
  site: Site;
  onOpen: (siteId: string) => void;
};

/**
 * One station of the list: a full-width button opening its sheet. Hovering or focusing it highlights its map pin.
 * The trailing chevron signals the row opens a detail view; it darkens and nudges right on hover/focus.
 */
const ListCard = forwardRef<HTMLButtonElement, Props>(({ site, onOpen }, ref) => {
  const { t } = useTranslation();
  const { dataView, hasRealTimeAccess, result } = useContext(SiteLookupContext);
  const { setHoveredSiteId } = useContext(SiteLookupMapContext);

  const isReportCapped = !!result?.data.limitExceeded;
  const pinVariant = getMapPinVariant(site, { isReportCapped });
  const exportStatus = getExportStatus(site, isReportCapped);
  const availability = hasRealTimeAccess && dataView === 'realtime' ? getAvailability(site.realTimeLocation) : null;
  const distance = t('siteLookup.list.distance', { count: +site.distance.toFixed(2) });

  return (
    <ButtonBase
      ref={ref}
      aria-label={t('siteLookup.list.stationLabel', { name: site.name, distance, status: t(mapPinVariantLabelKey[pinVariant]) })}
      onClick={() => onOpen(site.id)}
      onMouseEnter={() => setHoveredSiteId(site.id)}
      onMouseLeave={() => setHoveredSiteId(null)}
      onFocus={() => setHoveredSiteId(site.id)}
      onBlur={() => setHoveredSiteId(null)}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        width: '100%',
        paddingX: 1.5,
        paddingY: 1.5,
        borderRadius: '8px',
        textAlign: 'left',
        '&:hover': { backgroundColor: color.rowHover },
        '&.Mui-focusVisible': { outline: `2px solid ${color.primary}`, outlineOffset: -2 },
        '&:hover .ListCard-chevron, &.Mui-focusVisible .ListCard-chevron': { color: color.primary, transform: 'translateX(2px)' },
      }}
    >
      <NetworkIcon
        networkCode={site.networkCode}
        iconUrl={site.networkIconUrl ?? undefined}
        sx={{ borderRadius: 0.5, flexShrink: 0, height: size.rowLogo, width: size.rowLogo }}
      />

      <Box flex={1} minWidth={0}>
        <Typography fontSize={14} fontWeight={600} color={color.text.primary} sx={{ overflowWrap: 'anywhere', textTransform: 'capitalize' }}>
          {site.name.toLowerCase()}
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={0.75} marginTop={0.75}>
          {availability && <AvailabilityTag availability={availability} />}
          <StationTag>{getPortsLabel(site, t)}</StationTag>
          <StationTag>{t('siteLookup.list.power', { power: site.maxSpeed })}</StationTag>
          {exportStatus && <StationTag tone="warning">{t(`siteLookup.list.export.${exportStatus}`)}</StationTag>}
        </Box>
      </Box>

      <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
        <Typography fontSize={13} color={color.text.secondary} whiteSpace="nowrap">
          {distance}
        </Typography>
        <Box aria-hidden lineHeight={0}>
          <MapPin height={24} includedInReport={site.includedInReport} variant={pinVariant} width={24} />
        </Box>
        <ChevronRight aria-hidden className="ListCard-chevron" fontSize="small" sx={{ color: color.text.muted, transition: 'color 150ms, transform 150ms' }} />
      </Box>
    </ButtonBase>
  );
});

export default ListCard;
